/**
 * Booking Modal - Step-by-step appointment booking wizard
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useViewOnly } from '../../contexts';
import './Modal.css';
import './BookingModal.css';

const BookingModal = ({ isOpen, onClose, language = 'he', preselectedPatient = null }) => {
  const { authFetch } = useAuth();
  const { isViewOnly } = useViewOnly();
  const viewOnlyTip = language === 'he'
    ? 'מצב צפייה בלבד פעיל — בטל את המצב כדי לבצע שינויים'
    : 'View-only mode is on — disable to make changes';

  // Wizard state
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 1: Patient selection
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(preselectedPatient);
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  const [newPatient, setNewPatient] = useState({ name: '', phone: '', email: '' });

  // Step 2: Service selection
  const [categories, setCategories] = useState([]);
  const [selectedService, setSelectedService] = useState(null);

  // Step 3: Provider selection
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);

  // Step 4: Date selection
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  // Step 5: Time slot selection
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Step 6: Confirmation
  const [notes, setNotes] = useState('');
  const [sendSMS, setSendSMS] = useState(true);
  const [bookingResult, setBookingResult] = useState(null);

  const t = {
    he: {
      title: 'קביעת תור חדש',
      step1: 'בחירת מטופל',
      step2: 'בחירת שירות',
      step3: 'בחירת רופא',
      step4: 'בחירת תאריך',
      step5: 'בחירת שעה',
      step6: 'אישור',
      searchPatient: 'חפש מטופל לפי שם או טלפון...',
      minQueryHint: 'הקלד לפחות 2 תווים',
      noResults: 'לא נמצאו תוצאות',
      newPatient: 'מטופל חדש',
      patientName: 'שם מטופל',
      phone: 'טלפון',
      email: 'אימייל',
      selectService: 'בחר שירות',
      duration: 'משך',
      minutes: 'דקות',
      price: 'מחיר',
      selectProvider: 'בחר רופא/מטפל',
      selectDate: 'בחר תאריך',
      slotsAvailable: 'משבצות פנויות',
      selectTime: 'בחר שעה',
      noSlots: 'אין משבצות פנויות ביום זה',
      appointmentNotes: 'הערות לתור',
      sendSMSConfirmation: 'שלח SMS אישור למטופל',
      summary: 'סיכום',
      patient: 'מטופל',
      service: 'שירות',
      provider: 'מטפל',
      date: 'תאריך',
      time: 'שעה',
      back: 'חזור',
      next: 'הבא',
      confirm: 'אשר תור',
      booking: 'קובע תור...',
      success: 'התור נקבע בהצלחה!',
      error: 'שגיאה בקביעת התור',
      close: 'סגור',
      bookAnother: 'קבע תור נוסף',
      createPatient: 'צור מטופל',
      creating: 'יוצר...'
    },
    en: {
      title: 'Book New Appointment',
      step1: 'Select Patient',
      step2: 'Select Service',
      step3: 'Select Provider',
      step4: 'Select Date',
      step5: 'Select Time',
      step6: 'Confirm',
      searchPatient: 'Search patient by name or phone...',
      minQueryHint: 'Type at least 2 characters',
      noResults: 'No results found',
      newPatient: 'New Patient',
      patientName: 'Patient Name',
      phone: 'Phone',
      email: 'Email',
      selectService: 'Select a service',
      duration: 'Duration',
      minutes: 'minutes',
      price: 'Price',
      selectProvider: 'Select a provider',
      selectDate: 'Select a date',
      slotsAvailable: 'slots available',
      selectTime: 'Select a time',
      noSlots: 'No available slots on this day',
      appointmentNotes: 'Appointment notes',
      sendSMSConfirmation: 'Send SMS confirmation to patient',
      summary: 'Summary',
      patient: 'Patient',
      service: 'Service',
      provider: 'Provider',
      date: 'Date',
      time: 'Time',
      back: 'Back',
      next: 'Next',
      confirm: 'Confirm Booking',
      booking: 'Booking...',
      success: 'Appointment booked successfully!',
      error: 'Error booking appointment',
      close: 'Close',
      bookAnother: 'Book Another',
      createPatient: 'Create Patient',
      creating: 'Creating...'
    }
  }[language];

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSearchQuery('');
      setSearchResults([]);
      setSelectedPatient(preselectedPatient);
      setSelectedService(null);
      setSelectedProvider(null);
      setSelectedDate(null);
      setSelectedSlot(null);
      setNotes('');
      setSendSMS(true);
      setBookingResult(null);
      setError(null);
      setShowNewPatientForm(false);
      setNewPatient({ name: '', phone: '', email: '' });
    }
  }, [isOpen, preselectedPatient]);

  // Search patients
  const searchPatients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`/api/booking/search-patients?query=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.patients || []);
      }
    } catch (err) {
      console.error('Error searching patients:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  // Debounced search — gated on query.length >= 2 to avoid 1-char floods
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => {
      searchPatients(searchQuery);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery, searchPatients]);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/api/booking/services-by-category?language=${language}`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      setError('Error loading services');
    } finally {
      setLoading(false);
    }
  }, [authFetch, language]);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/api/booking/providers-for-service?service_id=${selectedService.id}`);
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers || []);
      }
    } catch (err) {
      setError('Error loading providers');
    } finally {
      setLoading(false);
    }
  }, [authFetch, selectedService]);

  const fetchAvailableDates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(
        `/api/booking/available-dates?provider_id=${selectedProvider.id}&service_id=${selectedService.id}&days_ahead=30`
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableDates(data.available_dates || []);
      }
    } catch (err) {
      setError('Error loading available dates');
    } finally {
      setLoading(false);
    }
  }, [authFetch, selectedProvider, selectedService]);

  const fetchTimeSlots = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(
        `/api/booking/slots-for-date?provider_id=${selectedProvider.id}&service_id=${selectedService.id}&date=${selectedDate.date}`
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.slots || []);
      }
    } catch (err) {
      setError('Error loading time slots');
    } finally {
      setLoading(false);
    }
  }, [authFetch, selectedProvider, selectedService, selectedDate]);

  // Fetch services when entering step 2
  useEffect(() => {
    if (step === 2 && categories.length === 0) {
      fetchServices();
    }
  }, [step, categories.length, fetchServices]);

  // Fetch providers when entering step 3
  useEffect(() => {
    if (step === 3 && selectedService) {
      fetchProviders();
    }
  }, [step, selectedService, fetchProviders]);

  // Fetch available dates when entering step 4
  useEffect(() => {
    if (step === 4 && selectedProvider && selectedService) {
      fetchAvailableDates();
    }
  }, [step, selectedProvider, selectedService, fetchAvailableDates]);

  // Fetch time slots when date is selected
  useEffect(() => {
    if (step === 5 && selectedDate && selectedProvider && selectedService) {
      fetchTimeSlots();
    }
  }, [step, selectedDate, selectedProvider, selectedService, fetchTimeSlots]);

  const createNewPatient = async () => {
    if (!newPatient.name || !newPatient.phone) {
      setError(language === 'he' ? 'נא למלא שם וטלפון' : 'Please enter name and phone');
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch('/api/patients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newPatient.name,
          phone: newPatient.phone,
          email: newPatient.email || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedPatient(data);
        setShowNewPatientForm(false);
        setNewPatient({ name: '', phone: '', email: '' });
        setStep(2);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error creating patient');
      }
    } catch (err) {
      setError('Error creating patient');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async () => {
    setLoading(true);
    setError(null);

    try {
      const startTime = new Date(`${selectedDate.date}T${selectedSlot.start_time}:00`);

      const response = await authFetch('/api/booking/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: selectedPatient.id,
          provider_id: selectedProvider.id,
          service_id: selectedService.id,
          start_time: startTime.toISOString(),
          notes: notes,
          send_sms_confirmation: sendSMS
        })
      });

      const data = await response.json();

      if (data.success) {
        setBookingResult({
          success: true,
          message: language === 'he' ? data.message_he : data.message,
          appointmentId: data.appointment_id
        });
      } else {
        setBookingResult({
          success: false,
          message: language === 'he' ? data.message_he : data.message,
          conflict: data.conflict
        });
      }
    } catch (err) {
      setBookingResult({
        success: false,
        message: t.error
      });
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return selectedPatient !== null;
      case 2: return selectedService !== null;
      case 3: return selectedProvider !== null;
      case 4: return selectedDate !== null;
      case 5: return selectedSlot !== null;
      default: return true;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content modal-large booking-modal" dir={language === 'he' ? 'rtl' : 'ltr'}>
        <div className="modal-header">
          <h2>{t.title}</h2>
          <button className="modal-close" onClick={onClose} title={language === 'he' ? 'סגור' : 'Close'}>×</button>
        </div>

        {/* Progress Steps */}
        <div className="booking-steps">
          {[1, 2, 3, 4, 5, 6].map((s) => (
            <div key={s} className={`step ${step === s ? 'active' : ''} ${step > s ? 'completed' : ''}`}>
              <div className="step-number">{s}</div>
              <div className="step-label">{t[`step${s}`]}</div>
            </div>
          ))}
        </div>

        <div className="modal-body booking-body">
          {error && <div className="alert alert-error">{error}</div>}

          {/* Step 1: Patient Selection */}
          {step === 1 && !bookingResult && (
            <div className="booking-step">
              <h3>{t.step1}</h3>

              {selectedPatient ? (
                <div className="selected-item">
                  <div className="item-info">
                    <strong>{selectedPatient.name}</strong>
                    <span>{selectedPatient.phone}</span>
                  </div>
                  <button className="btn-clear" onClick={() => setSelectedPatient(null)} title={language === 'he' ? 'נקה בחירה' : 'Clear selection'}>×</button>
                </div>
              ) : showNewPatientForm ? (
                <div className="new-patient-form">
                  <div className="form-group">
                    <label>{t.patientName} *</label>
                    <input
                      type="text"
                      value={newPatient.name}
                      onChange={(e) => setNewPatient({ ...newPatient, name: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t.phone} *</label>
                    <input
                      type="tel"
                      value={newPatient.phone}
                      onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })}
                      className="form-input"
                      dir="ltr"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t.email}</label>
                    <input
                      type="email"
                      value={newPatient.email}
                      onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
                      className="form-input"
                      dir="ltr"
                    />
                  </div>
                  <div className="form-actions">
                    <button className="btn btn-secondary" onClick={() => setShowNewPatientForm(false)}>
                      {t.back}
                    </button>
                    <button
                      className={`btn btn-primary ${isViewOnly ? 'disabled-view-only' : ''}`}
                      onClick={createNewPatient}
                      disabled={loading || isViewOnly}
                      title={isViewOnly ? viewOnlyTip : undefined}
                    >
                      {loading ? t.creating : t.createPatient}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="search-box">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t.searchPatient}
                      className="form-input"
                    />
                    {searchQuery.length > 0 && searchQuery.length < 2 && (
                      <div className="search-hint">{t.minQueryHint}</div>
                    )}
                  </div>

                  {loading && <div className="loading-spinner"></div>}

                  {searchResults.length > 0 && (
                    <div className="search-results-list">
                      {searchResults.map((patient) => (
                        <div
                          key={patient.id}
                          className="search-result-item"
                          onClick={() => {
                            setSelectedPatient(patient);
                            setSearchQuery('');
                            setSearchResults([]);
                          }}
                        >
                          <span className="patient-name">{patient.name}</span>
                          <span className="patient-phone">{patient.phone}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {searchQuery.length >= 2 && searchResults.length === 0 && !loading && (
                    <div className="no-results">{t.noResults}</div>
                  )}

                  <button
                    className="btn btn-outline new-patient-btn"
                    onClick={() => setShowNewPatientForm(true)}
                  >
                    + {t.newPatient}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Step 2: Service Selection */}
          {step === 2 && !bookingResult && (
            <div className="booking-step">
              <h3>{t.step2}</h3>

              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                <div className="services-list">
                  {categories.map((cat) => (
                    <div key={cat.category} className="service-category">
                      <h4>{cat.category}</h4>
                      <div className="services-grid">
                        {cat.services.map((service) => (
                          <div
                            key={service.id}
                            className={`service-card ${selectedService?.id === service.id ? 'selected' : ''}`}
                            onClick={() => setSelectedService(service)}
                          >
                            <div className="service-name">{service.name}</div>
                            <div className="service-details">
                              <span>{service.duration_minutes} {t.minutes}</span>
                              {service.price && <span>₪{service.price}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Provider Selection */}
          {step === 3 && !bookingResult && (
            <div className="booking-step">
              <h3>{t.step3}</h3>

              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                <div className="providers-grid">
                  {providers.map((provider) => (
                    <div
                      key={provider.id}
                      className={`provider-card ${selectedProvider?.id === provider.id ? 'selected' : ''}`}
                      onClick={() => setSelectedProvider(provider)}
                    >
                      <div className="provider-avatar" style={{ backgroundColor: provider.color || '#5BA4A4' }}>
                        {provider.name.charAt(0)}
                      </div>
                      <div className="provider-info">
                        <div className="provider-name">{provider.name}</div>
                        <div className="provider-role">{provider.role || provider.specialty}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Date Selection */}
          {step === 4 && !bookingResult && (
            <div className="booking-step">
              <h3>{t.step4}</h3>

              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                <div className="dates-grid">
                  {availableDates.map((dateInfo) => (
                    <div
                      key={dateInfo.date}
                      className={`date-card ${selectedDate?.date === dateInfo.date ? 'selected' : ''}`}
                      onClick={() => setSelectedDate(dateInfo)}
                    >
                      <div className="date-day">{language === 'he' ? dateInfo.day_name_he : dateInfo.day_name}</div>
                      <div className="date-value">{new Date(dateInfo.date).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US', { day: 'numeric', month: 'short' })}</div>
                      <div className="date-slots">{dateInfo.slots_count} {t.slotsAvailable}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 5: Time Slot Selection */}
          {step === 5 && !bookingResult && (
            <div className="booking-step">
              <h3>{t.step5}</h3>

              {loading ? (
                <div className="loading-spinner"></div>
              ) : availableSlots.length === 0 ? (
                <div className="no-slots">{t.noSlots}</div>
              ) : (
                <div className="slots-grid">
                  {availableSlots.map((slot, index) => (
                    <button
                      key={index}
                      className={`slot-btn ${selectedSlot?.start_time === slot.start_time ? 'selected' : ''}`}
                      onClick={() => setSelectedSlot(slot)}
                    >
                      {slot.start_time}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 6: Confirmation */}
          {step === 6 && !bookingResult && (
            <div className="booking-step">
              <h3>{t.summary}</h3>

              <div className="booking-summary">
                <div className="summary-row">
                  <span className="summary-label">{t.patient}:</span>
                  <span className="summary-value">{selectedPatient?.name}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.service}:</span>
                  <span className="summary-value">{selectedService?.name}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.provider}:</span>
                  <span className="summary-value">{selectedProvider?.name}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.date}:</span>
                  <span className="summary-value">{formatDate(selectedDate?.date)}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.time}:</span>
                  <span className="summary-value">{selectedSlot?.start_time} - {selectedSlot?.end_time}</span>
                </div>
                {selectedService?.price && (
                  <div className="summary-row">
                    <span className="summary-label">{t.price}:</span>
                    <span className="summary-value">₪{selectedService.price}</span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>{t.appointmentNotes}</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="form-textarea"
                  rows={3}
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={sendSMS}
                    onChange={(e) => setSendSMS(e.target.checked)}
                  />
                  {t.sendSMSConfirmation}
                </label>
              </div>
            </div>
          )}

          {/* Booking Result */}
          {bookingResult && (
            <div className="booking-result">
              <div className={`result-icon ${bookingResult.success ? 'success' : 'error'}`}>
                {bookingResult.success ? '✓' : '✗'}
              </div>
              <div className="result-message">{bookingResult.message}</div>
              {bookingResult.conflict && (
                <div className="conflict-info">
                  {language === 'he'
                    ? `כבר יש תור בשעה ${bookingResult.conflict.existing_start} - ${bookingResult.conflict.existing_end}`
                    : `Existing appointment at ${bookingResult.conflict.existing_start} - ${bookingResult.conflict.existing_end}`}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!bookingResult ? (
            <>
              {step > 1 && (
                <button className="btn btn-secondary" onClick={() => setStep(step - 1)}>
                  {t.back}
                </button>
              )}
              {step < 6 ? (
                <button
                  className="btn btn-primary"
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                >
                  {t.next}
                </button>
              ) : (
                <button
                  className={`btn btn-primary ${isViewOnly ? 'disabled-view-only' : ''}`}
                  onClick={handleBooking}
                  disabled={loading || isViewOnly}
                  title={isViewOnly ? viewOnlyTip : undefined}
                >
                  {loading ? t.booking : t.confirm}
                </button>
              )}
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose}>
                {t.close}
              </button>
              {bookingResult.success && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setBookingResult(null);
                    setStep(1);
                    setSelectedPatient(null);
                    setSelectedService(null);
                    setSelectedProvider(null);
                    setSelectedDate(null);
                    setSelectedSlot(null);
                    setNotes('');
                  }}
                >
                  {t.bookAnother}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
